// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// Rxjs
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { map, publishReplay, refCount, takeUntil } from 'rxjs/operators';

// Services
import { AuthService } from 'app/_services/auth.service';

// Environment
import { environment } from 'environments/environment';

// Models
import { User } from 'app/_models/user.model';
import { Login } from 'app/_models';
import { Route, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MDMService } from 'app/reusable/mdm.service';

@Injectable()
export class AuthenticationService implements OnDestroy {
    private bearerToken = '';
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    public roles$: Observable<any>;
    private token = '';
    private jwtHelper: JwtHelperService = new JwtHelperService();
    private _subject$: Subject<boolean> = new Subject();

    constructor(
        private router: Router,
        private httpClient: HttpClient,
        private authService: AuthService,
        private mdmService: MDMService
    ) {
        this.currentUserSubject = new BehaviorSubject<User>(new User());
        this.currentUser = this.currentUserSubject.asObservable();
        if (this.authService.getToken()) {
            this.setCurrentUser(this.authService.getToken());
        }
    }


    public get currentUserValue(): User {
        this.token = localStorage.getItem(environment.TokenName);

        if (this.token && JSON.parse(this.token)['token']) {
            this.setCurrentUser(JSON.parse(this.token)['token']);
        }
        return this.currentUserSubject.value;
    }

    public createAuthorizationHeader(headers: Headers) {
        this.bearerToken = this.authService.getToken();

        headers.append('Authorization', 'Bearer ' + this.bearerToken);
    }

    public getProfileRoles(): Promise<any> {
        const BASE_URL = environment.Authentication.authApiUrl;
        const sUrl = `${BASE_URL}/roles`;
        this.bearerToken = this.authService.getToken();

        let headersJSON = new HttpHeaders();
        headersJSON = headersJSON
            .set('Content-Type', 'application/json; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.bearerToken);

        return this.httpClient
            .get(sUrl, { headers: headersJSON })
            .toPromise()
            .then(response => {
                return response || {};
            });
    }

    public getRoles(): Observable<any> {
        const BASE_URL = environment.Authentication.authApiUrl;
        const sUrl = `${BASE_URL}roles`;

        this.bearerToken = this.authService.getToken();

        let headersJSON = new HttpHeaders();
        headersJSON = headersJSON
            .set('Content-Type', 'application/json; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.bearerToken);

        if (!this.roles$) {
            this.roles$ = this.httpClient
                .get(sUrl, { headers: headersJSON })
                .pipe(
                    map(data => data),
                    publishReplay(1),
                    refCount()
                );
        }

        return this.roles$;
    }

    public login(loginData: Login) {

        const sUrl = environment.Authentication.LoginURL;

        return this.httpClient
            .post<any>(sUrl, loginData)
            .pipe(map(user => {
                localStorage.setItem(environment.TokenName, JSON.stringify({ token: user }));
                this.currentUserSubject.next(user);

                return user;
            }));
    }

    public authenticate() {
        const returnUrl = encodeURIComponent(`https://${location.host}/#/` + environment.pageUrls.login);
        const url = environment.Authentication.authUrl + returnUrl;
        window.location.href = url;
    }

    registerAuth(token) {
        let _tokenDecoded = this.jwtHelper.decodeToken(token);
        if (_tokenDecoded) {
            localStorage.setItem(environment.TokenName, JSON.stringify({ token: token }));
            forkJoin([
                this.mdmService.getUserRoles(),
                this.mdmService.getUserFunctionnalities()
            ])
                .pipe(takeUntil(this._subject$))
                .subscribe((results: any[]) => {
                    localStorage.setItem(environment.RolesName, JSON.stringify({ data: results[0] }));
                    localStorage.setItem(environment.Functionnalities, JSON.stringify({ data: results[1] }));
                    this.currentUserSubject.next(token);                    
                    this.router.navigate([environment.pageUrls.main]);
                });

        }
    }

    getUserRoles() {
        this.mdmService
            .getUserRoles()
            .pipe(map(data => {
                localStorage.setItem(environment.RolesName, JSON.stringify({ data: data }));
            })).subscribe();
    }

    getFunctionnalities() {
        this.mdmService
            .getUserFunctionnalities()
            .pipe(map(data => {
                localStorage.setItem(environment.Functionnalities, JSON.stringify({ data: data }));
            })).subscribe();
    }

    public logout() {
        localStorage.removeItem(environment.TokenName);
        localStorage.clear();
        localStorage.removeItem(environment.ReturnUrlName);
        localStorage.removeItem(environment.RolesName);
        localStorage.removeItem(environment.Functionnalities);
        localStorage.removeItem(environment.GenesysTokenName);        

        this.currentUserSubject.next(null);
    }

    private setCurrentUser(token) {
        let _user = new User();
        let _tokenDecoded = this.jwtHelper.decodeToken(token);
        if (_tokenDecoded) {
            _user.given_name = _tokenDecoded.given_name;
            _user.ipaddr = _tokenDecoded.ipaddr;
            _user.name = _tokenDecoded.name;
            _user.unique_name = _tokenDecoded.unique_name;
            _user.token = token;
            _user.family_name = _tokenDecoded.family_name;
            _user.tid = _tokenDecoded.tid;
        }

        this.currentUserSubject = new BehaviorSubject<User>(_user);
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public getLocalStorageRoles() {
        let roles = localStorage.getItem(environment.RolesName);
        if (roles)
            return JSON.parse(roles)['data'];
        else
            return null;
    }

    ngOnDestroy(): void {
        this._subject$.next();
        this._subject$.complete();
    }
}
